import React, { useContext, useState, useEffect, useRef } from "react";
import { ApiContext } from "../../../../context/MyContext";
import {
  createPlan,
  modifyPlanTrackerMappings,
} from "../../../../service/pricingPlanService";
import { Toaster } from "../../../../common/Toaster/Toaster";
import TrackerMultiSelect from "../../../../common/TrackerMultiSelect/TrackerMultiSelect";
import { useModalExit } from "../../../../common/comms/Modal";

const AddPlanModal = ({ trackers = [], onClose, onSuccess }) => {
  const { post } = useContext(ApiContext);

  const [form, setForm] = useState({
    planName: "",
    planDetails: [""],
    selectedTrackerIds: [],
  });

  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const { leaving, close, leaveThen } = useModalExit({ onClose, blocked: loading });

  // Auto hide toast
  useEffect(() => {
    if (toastMessage) {
      const timeout = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timeout);
    }
  }, [toastMessage]);

  // Plan details handlers
  const handlePlanDetailChange = (idx, value) => {
    const updated = [...form.planDetails];
    updated[idx] = value;
    setForm((prev) => ({ ...prev, planDetails: updated }));
  };

  const addPlanDetail = () => {
    setForm((prev) => ({
      ...prev,
      planDetails: [...prev.planDetails, ""],
    }));
  };

  const removePlanDetail = (idx) => {
    setForm((prev) => ({
      ...prev,
      planDetails: prev.planDetails.filter((_, i) => i !== idx),
    }));
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setToastMessage(null);

    if (
      !form.planName.trim() ||
      !form.planDetails.length ||
      form.planDetails.some((d) => !d.trim())
    ) {
      setToastMessage("Please fill all required plan details.");
      setToastType("error");
      setLoading(false);
      return;
    }

    try {
      // Build payload with CREATE operation (no id field)
      const payload = {
        event_type_id: "4850387a-8838-45d5-8c65-dce9d1f86de6",
        plans: [
          {
            operation: "ADD",
            plan_name: form.planName.trim(),
            details: form.planDetails
              .filter((d) => d.trim())
              .join(", "), // Join details into a single string
          },
        ],
      };

      const response = await createPlan(post, payload);

      // Trackers are mapped in a second call, so the plan stays created even if this fails
      const planId = response?.data?.data?.[0]?.id;
      if (planId && form.selectedTrackerIds.length) {
        try {
          await modifyPlanTrackerMappings(post, {
            event_type_id: "4850387a-8838-45d5-8c65-dce9d1f86de6",
            plan_tracker_mappings: form.selectedTrackerIds.map((trackerId) => ({
              operation: "ADD",
              plan_id: planId,
              tracker_id: trackerId,
            })),
          });
        } catch (trackerErr) {
          console.error(trackerErr);
          setToastMessage("Plan created, but trackers couldn't be saved — edit the plan to retry.");
          setToastType("error");
          setTimeout(() => onSuccess && leaveThen(onSuccess), 2500);
          return;
        }
      }

      setToastMessage("Plan created successfully!");
      setToastType("success");

      setTimeout(() => onSuccess && leaveThen(onSuccess), 1200);
    } catch (err) {
      console.error(err);
      setToastMessage(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to create plan."
      );
      setToastType("error");
    } finally {
      setLoading(false);
    }
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && !loading) {
        close();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [close, loading]);

  // Focus management - focus the first input on mount
  const planNameInputRef = useRef(null);
  useEffect(() => {
    if (planNameInputRef.current) {
      planNameInputRef.current.focus();
    }
  }, []);

  return (
    <div
      className={`fixed inset-0 bg-black/20 flex justify-center items-center z-50 ${
        leaving
          ? "pointer-events-none motion-safe:animate-modal-overlay-out motion-reduce:opacity-0"
          : "motion-safe:animate-modal-overlay-in"
      }`}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-plan-modal-title"
    >
      <div
        className={`bg-white p-6 rounded-xl shadow-lg w-[500px] max-w-[600px] h-auto max-h-[90vh] overflow-y-auto relative ${
          leaving ? "motion-safe:animate-modal-panel-out" : "motion-safe:animate-modal-panel-in"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-2 right-3 text-2xl font-bold text-gray-500 hover:text-gray-700 transition-colors"
          onClick={close}
          disabled={loading}
          aria-label="Close modal"
        >
          ×
        </button>

        <h2 id="add-plan-modal-title" className="text-xl font-bold mb-4 text-gray-800">Add New Plan</h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="plan-name-input" className="block font-semibold mb-1 text-gray-700">
              Plan Name<span className="text-red-500">*</span>
            </label>
            <input
              id="plan-name-input"
              ref={planNameInputRef}
              type="text"
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-primary"
              value={form.planName}
              onChange={(e) => handleChange("planName", e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block font-semibold mb-1 text-gray-700">
              Plan Details<span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col gap-2">
              {form.planDetails.map((detail, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    className="border px-3 py-2 rounded w-full"
                    value={detail}
                    onChange={(e) => handlePlanDetailChange(idx, e.target.value)}
                    required
                  />
                  {form.planDetails.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePlanDetail(idx)}
                      className="text-red-500 text-xl font-bold hover:text-red-700"
                      title="Remove"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="text-orange-primary hover:text-orange-600 mt-1 font-semibold text-left"
                onClick={addPlanDetail}
              >
                + Add Detail
              </button>
            </div>
          </div>

          <TrackerMultiSelect
            trackers={trackers}
            selectedIds={form.selectedTrackerIds}
            onChange={(ids) => handleChange("selectedTrackerIds", ids)}
            disabled={loading}
          />

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={close}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md transition-colors ${
                !loading
                  ? "bg-orange-primary text-white hover:bg-orange-600"
                  : "bg-gray-300 text-gray-800 cursor-not-allowed"
              }`}
              disabled={loading}
              aria-label={loading ? "Creating plan..." : "Create plan"}
            >
              {loading ? "Creating..." : "Create Plan"}
            </button>
          </div>

          {toastMessage && <Toaster message={toastMessage} type={toastType} />}
        </form>
      </div>
    </div>
  );
};

export default AddPlanModal;
